import { supabase } from "./supabase";
import type { Oekt, Rolle } from "./session";

/**
 * Oppsettet av produksjonen.
 *
 * ========  ÉN SIDE, OG DU SER DIN DEL AV DEN  ========
 *
 * Brukeren 27.10.2026: «Hvis du da står som redaktør, så får du opp valgene
 * om å legge til flere deltakere, er du A-foto, så kan du definere FPS,
 * oppløsning og slikt, er du prodleder får du valget om å legge til teams,
 * godkjenne brukere med spesialavtaler, og alt annet som trenger å
 * defineres.»
 *
 * Det er samme regel som ellers i flata: rollen gir ett element mer, ikke
 * en annen flate. Én side å vedlikeholde, én å lære.
 *
 * ========  DET SOM MANGLER AVGJØR OM SIDA ÅPNER SEG  ========
 *
 * «Dette er den samme siden som kommer opp etter første innlogging.» Den
 * åpner seg av seg selv når noe som MÅ finnes ikke gjør det — og bare for
 * den som kan gjøre noe med det. En redaktør skal ikke møte en liste over
 * ting prodleder har glemt.
 */

export type Felt = "produksjon" | "teknisk" | "dager" | "team" | "godkjenning"
  | "deltakere";

/** Hvem som får gjøre hva. Speiler policyene — basen er fasiten. */
export function minePunkter(
  rolle: Rolle, ekstra: readonly Rolle[] = [],
): readonly Felt[] {
  /* EN PERSON KAN BAERE FLERE FAGFUNKSJONER i samme produksjon, og
     rollene de gir teller like mye som hovedrollen — basen gjoer ingen
     forskjell i `tilgang.har_rolle`. Uten dette ville en koordinator
     som OGSAA er prodleder blitt nektet Oppsett i flata, av noe basen
     ville sluppet gjennom. Se migrasjon 20261101110000. */
  const mine = [rolle, ...ekstra];
  const har = (r: Rolle): boolean => mine.includes(r);
  const setterOpp = har("prodleder") || har("produsent");
  const punkter: Felt[] = [];
  if (setterOpp) punkter.push("produksjon", "dager", "team", "godkjenning");
  /* A-FOTO EIER DET TEKNISKE, ikke hele teknikergruppa. Det er hen som
     legger inn utstyret og setter takten for produksjonen — én person, og
     ikke en avdeling. Prodleder og produsent ser det også; vernet ligger i
     basen uansett. Avgjort 27.10.2026, se migrasjon 20261027190100. */
  if (setterOpp || har("afoto")) punkter.push("teknisk");
  if (setterOpp || har("redaktor")) punkter.push("deltakere");
  return punkter;
}

export interface Mangler {
  readonly dager: boolean;
  readonly team: boolean;
  readonly deltakere: boolean;
  readonly godkjenning: boolean;
}

/**
 * Hva som ikke er satt opp ennå.
 *
 * Teller rader, og holder seg til det som MÅ finnes for at en dag kan
 * kjøres: opptaksdager (ellers finnes ingen dag å henge noe på), team
 * (ellers deler ingen rapport), deltakere (ellers har loggen ingen
 * forkortelser å vise).
 */
export async function hentMangler(oekt: Oekt): Promise<Mangler> {
  const tom: Mangler = {
    dager: false, team: false, deltakere: false, godkjenning: false,
  };
  const kl = supabase;
  if (kl === null) return tom;
  const p = oekt.production.id;
  const tell = async (tabell: string): Promise<number> => {
    const { count } = await kl
      .from(tabell)
      .select("*", { count: "exact", head: true })
      .eq("production_id", p);
    return count ?? 0;
  };
  const [dager, team, deltakere, venter] = await Promise.all([
    tell("shoot_day"), tell("unit"), tell("participant"),
    tell("venter_paa_godkjenning"),
  ]);
  return {
    dager: dager === 0,
    team: team === 0,
    deltakere: deltakere === 0,
    godkjenning: venter > 0,
  };
}

/** Er det noe JEG kan gjøre noe med? Bare da åpner sida seg selv. */
export function angaarMeg(m: Mangler, mine: readonly Felt[]): boolean {
  return (m.dager && mine.includes("dager"))
    || (m.team && mine.includes("team"))
    || (m.deltakere && mine.includes("deltakere"))
    || (m.godkjenning && mine.includes("godkjenning"));
}

/* ---- produksjonen ------------------------------------------------- */

export async function lagreProduksjon(
  produksjonId: string, felt: Record<string, unknown>,
): Promise<{ ok: boolean; feil?: string }> {
  if (supabase === null) return { ok: true };
  const { error } = await supabase
    .from("production").update(felt).eq("id", produksjonId);
  return error === null ? { ok: true } : { ok: false, feil: error.message };
}

/* ---- team ---------------------------------------------------------- */

export type Teamslag = "let" | "vj" | "control_room";

export async function leggTilTeam(
  produksjonId: string, navn: string, slag: Teamslag,
): Promise<{ ok: boolean; feil?: string }> {
  if (supabase === null) return { ok: true };
  const { error } = await supabase
    .from("unit")
    .insert({ production_id: produksjonId, name: navn.trim(), kind: slag });
  return error === null ? { ok: true } : { ok: false, feil: error.message };
}

export async function fjernTeam(id: string): Promise<boolean> {
  if (supabase === null) return true;
  const { error } = await supabase.from("unit").delete().eq("id", id);
  return error === null;
}

/* ---- opptaksdager --------------------------------------------------- */

export interface Dag {
  readonly dag: string;
  /** Null for en dag det finnes logg paa uten kalenderrad. Se hentDager. */
  readonly dato: string | null;
  readonly notat: string | null;
}

/**
 * Naar opptaksdagen begynner og slutter.
 *
 * ========  HVORFOR KLOKKESLETT OG IKKE BARE DATO  ========
 *
 * Brukeren 02.11.2026: «Dette gjoer vi i tilfelle en opptaksdag gaar
 * over 23:59:59 paa kvelden.» Et nattskift som begynner 18:00 og
 * slutter 03:30 er ÉN opptaksdag med to datoer, og appen maa kunne vite
 * hvilken dag den logger paa klokka 01:00.
 *
 * Datoen blir staaende som dagens identitet. Dette er naar den GAAR.
 */
export async function settDagsvindu(
  produksjonId: string, dag: string,
  startIso: string | null, sluttIso: string | null,
): Promise<{ ok: boolean; feil?: string }> {
  if (supabase === null) return { ok: true };
  const { error } = await supabase
    .from("shoot_day")
    .update({ starts_at: startIso, ends_at: sluttIso })
    .eq("production_id", produksjonId)
    .eq("day", dag);
  return error === null ? { ok: true } : { ok: false, feil: error.message };
}

/**
 * Naar dokumentene for en opptaksdag tar over hovedvisningen i appen.
 *
 * ========  DET ER FORRIGE DAGS SLUTT  ========
 *
 * Brukeren 02.11.2026: «Paa opptaksdag 2 (i dag) satt jeg at den
 * dagsplanen er ferdig klokken 04:00 neste dag. Da oensker jeg at
 * klokkeslettet dagsplanen for opptaksdag 3 tar over hovedvisningen i
 * appen vises.»
 *
 * Appen staar i en opptaksdag til den dagen er over — `dagen_naa`, se
 * migrasjon 20261102110000 — saa neste dags dokumenter tar over naar
 * FORRIGE dag slutter. Har forrige dag ikke noe sluttidspunkt, ruller
 * dagen ved midnatt, og da er det midnatt foran datoen som gjelder.
 *
 * Null naar dagen ikke finnes i kalenderen: da kan ingen vite det.
 */
export async function visesFra(
  produksjonId: string, dag: string,
): Promise<Date | null> {
  if (supabase === null) return null;
  const { data } = await supabase
    .from("shoot_day")
    .select("day, date, ends_at")
    .eq("production_id", produksjonId)
    .order("date", { ascending: true });

  const dager = (data ?? []) as unknown as readonly {
    day: string; date: string; ends_at: string | null;
  }[];
  const min = dager.find((d) => d.day === dag);
  if (min === undefined) return null;

  /* Forrige dag er den siste FOER denne datoen. Lista er sortert. */
  const foer = [...dager].reverse().find((d) => d.date < min.date);
  if (foer?.ends_at != null) return new Date(foer.ends_at);
  return new Date(`${min.date}T00:00:00`);
}

/** Vinduet for én dag, eller null paa begge naar ingen har satt det. */
export async function hentDagsvindu(
  produksjonId: string, dag: string,
): Promise<{ start: string | null; slutt: string | null; dato: string } | null> {
  if (supabase === null) return null;
  const { data } = await supabase
    .from("shoot_day")
    .select("date, starts_at, ends_at")
    .eq("production_id", produksjonId)
    .eq("day", dag)
    .maybeSingle();
  if (data === null) return null;
  const r = data as unknown as Record<string, unknown>;
  return {
    dato: String(r["date"]),
    start: r["starts_at"] == null ? null : String(r["starts_at"]),
    slutt: r["ends_at"] == null ? null : String(r["ends_at"]),
  };
}

/**
 * Dagene det finnes LOGG paa, uansett om de staar i kalenderen.
 *
 * ========  EN LOGG SKAL ALLTID KUNNE NAAS  ========
 *
 * Dagpilene leste bare `shoot_day`. En logg som havnet paa en dag som
 * ikke staar i kalenderen var dermed uten vei: ingen flate kunne bla
 * dit, og den som skrev postene kunne ikke trekke dem tilbake heller.
 *
 * Det er ikke en teoretisk tilstand. Appen arvet opptaksdagen fra
 * produksjonen man sto i fram til 02.11.2026, og skrev dermed logg paa
 * dag 7 i en produksjon som bare hadde dag 1 og 2. Feilen er rettet,
 * men postene ble liggende — og en logg man ikke kan naa kan ingen
 * rydde.
 *
 * Dagene herfra staar uten dato. Flata viser «Dag 7» for dem, og det er
 * riktig: de ER ikke planlagt, de er skrevet.
 */
async function dagerMedLogg(produksjonId: string): Promise<readonly string[]> {
  if (supabase === null) return [];
  const { data } = await supabase
    .from("log_session")
    .select("shoot_day")
    .eq("production_id", produksjonId);
  const sett = new Set<string>();
  for (const r of (data ?? []) as readonly Record<string, unknown>[]) {
    sett.add(String(r["shoot_day"]));
  }
  return [...sett];
}

export async function hentDager(produksjonId: string): Promise<readonly Dag[]> {
  if (supabase === null) return [];
  const [{ data }, medLogg] = await Promise.all([
    supabase
      .from("shoot_day")
      .select("day, date, note")
      .eq("production_id", produksjonId)
      .order("date", { ascending: true }),
    dagerMedLogg(produksjonId),
  ]);
  if (data === null) return [];
  const kalenderen = (data as unknown as Record<string, unknown>[]).map((r) => ({
    dag: String(r["day"]),
    dato: String(r["date"]) as string | null,
    notat: r["note"] == null ? null : String(r["note"]),
  }));

  /* Dager det finnes logg paa, men ingen kalenderrad for. De staar sist
     og uten dato — de er ikke planlagt, de er skrevet. Se dagerMedLogg. */
  const kjente = new Set(kalenderen.map((d) => d.dag));
  const foreldreloese = medLogg
    .filter((d) => !kjente.has(d))
    .sort()
    .map((d) => ({ dag: d, dato: null, notat: null }));

  return [...kalenderen, ...foreldreloese];
}

export async function leggTilDag(
  produksjonId: string, dag: string, dato: string,
): Promise<{ ok: boolean; feil?: string }> {
  if (supabase === null) return { ok: true };
  const { error } = await supabase
    .from("shoot_day")
    .insert({ production_id: produksjonId, day: dag.trim(), date: dato });
  return error === null ? { ok: true } : { ok: false, feil: error.message };
}

export async function fjernDag(produksjonId: string, dag: string): Promise<boolean> {
  if (supabase === null) return true;
  const { error } = await supabase
    .from("shoot_day").delete()
    .eq("production_id", produksjonId).eq("day", dag);
  return error === null;
}
